import { motion } from "framer-motion";
import { Plane, Car, Ship, Trophy, Sparkles } from "lucide-react";
import { formatCr } from "../lib/format";

const LADDER_ICONS = { plane: Plane, car: Car, ship: Ship };

const ProgressBar = ({ label, achieved, target, pct, achiever, testid }) => (
  <div data-testid={testid}>
    <div className="flex items-end justify-between mb-2">
      <span className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#8A8A82]">{label}</span>
      {achiever && (
        <span className="inline-flex items-center gap-1 font-grotesk text-[9px] uppercase tracking-[0.18em] text-[#2FB999]">
          <Trophy size={11} /> Achieved
        </span>
      )}
    </div>
    <div className="flex items-baseline justify-between mb-2">
      <span className="font-serif text-2xl">{formatCr(achieved)}</span>
      <span className="font-body text-xs text-[#8A8A82]">of {formatCr(target)}</span>
    </div>
    <div className="h-2 rounded-full bg-white/10 overflow-hidden">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${Math.min(pct, 100)}%` }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="h-full rounded-full"
        style={{ background: achiever ? "#2FB999" : "#B87333" }}
      />
    </div>
    <div className="font-body text-[10px] text-[#6f6f68] mt-1.5">{pct}% of target</div>
  </div>
);

export const RewardsPanel = ({ data }) => {
  if (!data) return null;
  return (
    <div className="mt-16" data-testid="rewards-panel">
      <div className="flex items-center gap-3 mb-2">
        <span className="grid place-items-center w-9 h-9 rounded-full bg-[#B87333] text-black"><Sparkles size={17} /></span>
        <h2 className="font-serif text-2xl md:text-3xl">Rewards &amp; Recognition</h2>
      </div>
      <p className="font-body text-sm text-[#8A8A82] mb-6 max-w-xl leading-relaxed">
        Your turnover with Vinci counts towards monthly &amp; annual targets for the{" "}
        <span className="text-[#F5F5F0]">{data.tier_label}</span> tier
        {data.top_state ? ` (${data.top_state} · ${data.state_group} state)` : ` (${data.state_group} state target)`}.
        Hit your targets to unlock the rewards below.
      </p>

      <div className="grid md:grid-cols-2 gap-8 border border-white/10 rounded-sm p-6 md:p-8">
        <ProgressBar testid="reward-monthly" label={`This month · ${data.month_label}`} achieved={data.monthly_achieved} target={data.monthly_target} pct={data.monthly_pct} achiever={data.monthly_achiever} />
        <ProgressBar testid="reward-annual" label={`This year · ${data.year_label}`} achieved={data.annual_achieved} target={data.annual_target} pct={data.annual_pct} achiever={data.annual_achiever} />
      </div>

      <h3 className="font-grotesk text-[11px] uppercase tracking-[0.2em] text-[#B87333] mt-10 mb-4">The reward ladder</h3>
      <div className="grid md:grid-cols-3 gap-4" data-testid="rewards-ladder">
        {data.ladder.map((r, i) => {
          const Icon = LADDER_ICONS[r.icon] || Trophy;
          return (
            <motion.div
              key={r.key}
              data-testid={`ladder-${r.key}`}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="rounded-sm border border-white/10 p-6 hover:border-[#B87333]/60 transition-colors bg-[#0a0a0a]"
            >
              <span className="grid place-items-center w-11 h-11 rounded-full bg-[#141414] border border-white/10 text-[#B87333]"><Icon size={19} /></span>
              <div className="font-grotesk text-[9px] uppercase tracking-[0.18em] text-[#8A8A82] mt-4">{r.level}</div>
              <div className="font-serif text-xl mt-1">{r.title}</div>
              <ul className="mt-3 space-y-1.5">
                {r.prizes.map((p) => (
                  <li key={p} className="font-body text-sm text-[#C9C9C2] flex gap-2">
                    <span className="text-[#B87333] mt-0.5">·</span>{p}
                  </li>
                ))}
              </ul>
            </motion.div>
          );
        })}
      </div>
      <p className="font-body text-[10px] text-[#6f6f68] mt-4">Rewards are indicative and subject to programme terms, verification of achieved turnover, and management confirmation.</p>
    </div>
  );
};
